<script setup lang="ts">
    import type { UIChatMessage } from 'models'
    import { DefaultChatTransport } from 'ai'
    import { computed, ref } from 'vue'
    import { useI18n } from 'vue-i18n'
    import { generateId } from 'ai'
    import { Chat } from '@ai-sdk/vue'
    import PkAvatar from './PkAvatar.vue'
    import PkStreamingMarkdown from './PkStreamingMarkdown.vue'

    const { t: $t } = useI18n({
        useScope: 'global',
    })

    const props = defineProps<{
        baseUrl: string
        agentId: string
        name?: string
        model?: string
        temperature?: number
        headers?: Record<string, string>
    }>()

    const emit = defineEmits<{
        (e: 'show-info', message: UIChatMessage): void
    }>()

    const input = ref('')

    const chat = computed(
        () =>
            new Chat({
                id: generateId(),
                transport: new DefaultChatTransport({
                    api: `${props.baseUrl}/agents/${props.agentId}/reasoning-chat`,
                    prepareSendMessagesRequest: ({ messages }) => {
                        return {
                            body: {
                                messages: [messages[messages.length - 1]],
                                model: props.model,
                                temperature: props.temperature,
                            },
                        }
                    },
                    credentials: 'include',
                    headers: props.headers,
                }),
            }),
    )

    const handleSubmit = () => {
        if (!input.value.trim() || isLoading.value) {
            return
        }
        chat.value.sendMessage({ text: input.value })
        input.value = ''
    }

    const handleKeydown = (e: KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault()
            handleSubmit()
        }
    }

    const resetAnswer = () => {
        chat.value.messages = []
        chat.value.clearError()
        input.value = ''
    }

    const isLoading = computed(() => {
        return (
            chat.value.status === 'submitted' ||
            chat.value.status === 'streaming'
        )
    })

    const hasAnswer = computed(() => {
        return chat.value.messages.some((m) => m.role === 'assistant')
    })

    const lastAssistantMessage = computed(() => {
        const messages = chat.value.messages
        for (let i = messages.length - 1; i >= 0; i--) {
            if (messages[i].role === 'assistant') {
                return messages[i]
            }
        }
        return undefined
    })

    const textContent = computed(() => {
        return (
            lastAssistantMessage.value?.parts.find((p) => p.type === 'text')
                ?.text || ''
        )
    })
</script>

<template>
    <div class="flex flex-col h-full bg-surface rounded-xl">
        <!-- header -->
        <div
            class="sticky z-1 top-0 bg-surface-1 flex items-center justify-between gap-sm p-sm border-b border-surface-4">
            <div class="flex items-center gap-sm min-w-0">
                <PkAvatar
                    modifiers="surface ring shrink-0"
                    class="w-24 h-24"
                    :name />
                <strong
                    v-if="name"
                    class="text-14 font-bold truncate"
                    :title="name">
                    {{ name }}
                </strong>
                <strong v-else class="text-14 font-bold">
                    {{ $t('label.reasoningAgentTester') }}
                </strong>
            </div>
            <VvButton
                v-if="hasAnswer"
                icon="ri:reset-right-line"
                modifiers="action-quiet"
                :title="$t('action.clear')"
                :disabled="isLoading"
                @click="resetAnswer" />
        </div>

        <!-- content -->
        <div class="flex-1 flex flex-col gap-md p-md overflow-y-auto min-h-0">
            <!-- input -->
            <div class="flex flex-col">
                <label class="text-12 font-medium text-word-2 mb-xs block">
                    {{ $t('label.question') }}
                </label>
                <textarea
                    v-model="input"
                    :placeholder="$t('placeholder.enterYourQuestion')"
                    :disabled="isLoading"
                    class="u-field w-full p-sm font-inherit text-14 resize-y min-h-60"
                    rows="3"
                    @keydown="handleKeydown" />
                <div class="flex justify-end mt-xs">
                    <VvButton
                        :label="$t('action.submit')"
                        :loading="isLoading"
                        :disabled="!input.trim() || isLoading"
                        @click="handleSubmit" />
                </div>
            </div>

            <!-- error -->
            <div
                v-if="chat.error"
                class="flex items-start gap-sm p-md bg-danger-lighten-4 border border-danger rounded-xl text-danger-darken-2">
                <VvIcon
                    name="ri:error-warning-line"
                    class="w-16 h-16 shrink-0" />
                <div>
                    <div class="font-bold">{{ $t('label.error') }}</div>
                    <div class="text-12">{{ chat.error.message }}</div>
                </div>
            </div>

            <!-- loading -->
            <div
                v-if="isLoading && !lastAssistantMessage"
                class="flex items-center gap-sm p-md bg-surface-1 border border-surface-3 rounded-xl text-word-2">
                <VvIcon name="line-md:loading-loop" class="w-20 h-20" />
                <span>{{ $t('label.processing') }}</span>
            </div>

            <!-- answer -->
            <div
                v-if="lastAssistantMessage"
                class="flex flex-col gap-sm p-md bg-surface-1 border border-surface-3 rounded-xl">
                <div
                    class="flex items-center gap-xs pb-xs border-b border-surface-3">
                    <VvIcon name="ri:robot-2-line" class="w-16 h-16" />
                    <span class="text-12 font-medium text-word-2">
                        {{ $t('label.answer') }}
                    </span>
                    <div class="flex items-center gap-xs ml-auto">
                        <VvIcon
                            v-if="isLoading"
                            name="line-md:loading-loop"
                            class="w-14 h-14" />
                        <VvButton
                            v-if="lastAssistantMessage.parts.length > 0"
                            icon="ri:information-line"
                            :title="$t('label.reasoningDetails')"
                            modifiers="action-quiet-small"
                            @click.stop="
                                emit(
                                    'show-info',
                                    lastAssistantMessage as unknown as UIChatMessage,
                                )
                            " />
                    </div>
                </div>
                <div
                    v-if="textContent"
                    class="py-sm text-word-2 leading-normal">
                    <PkStreamingMarkdown
                        :markdown="textContent"
                        :loading="isLoading"
                        class="wysiwyg text-14" />
                </div>
            </div>
        </div>
    </div>
</template>
